import React, { type ComponentProps, useCallback, useRef } from 'react';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { Alert, ScrollView, StyleSheet, TouchableOpacity, View } from 'react-native';

import {
  BodyText,
  Button,
  Container,
  DisplayText,
  Form,
  Icon,
  IconLink,
  theme,
  useHovered,
  useResponsive,
  webOnlyStyles,
} from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';

import { LoadingAnimation, NotFoundView, SignedOutView, TransparentModal } from '../../components';
import { useIsLoggedIn, useIsLoggingIn } from '../../context/AuthMachine';
import { useGroupOrderCreateOrderMutation } from './graphql/GroupOrderLocation.generated';
import { GroupOrderChannel, GroupOrderRouteParams } from './GroupOrderSetupScreen.types';
import {
  type GroupOrderSetupFormData,
  useGroupOrderLocationDetails,
  useGroupOrderSetupForm,
} from './hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const GroupOrderSetupScreen = () => {
  const isGroupOrderingEnabled = useFeatureFlag('CELS-5355-order-group-ordering-enabled');

  const isLoggedIn = useIsLoggedIn();
  const isLoggingIn = useIsLoggingIn();

  // ─────────────────────────────────────────────────────────────────────

  if (!isGroupOrderingEnabled) {
    return <NotFoundView />;
  }

  if (isLoggingIn) {
    return <LoadingAnimation />;
  }

  if (!isLoggedIn) {
    return <SignedOutView withoutFooter />;
  }

  return <GroupOrderSetupScreenContent />;
};

// ─────────────────────────────────────────────────────────────────────────────

const GroupOrderSetupScreenContent = () => {
  const router = useRouter();
  const { formatMessage } = useIntl();
  const { match } = useResponsive();

  // ─── Params ──────────────────────────────────────────────────────────

  const params = useLocalSearchParams<GroupOrderRouteParams>();

  const { restaurantId, deliveryAddressId } = params ?? {};

  // ─── Refs ────────────────────────────────────────────────────────────

  const locationToggleRef = useRef(null);
  const isLocationRowHovered = useHovered(locationToggleRef);

  // ─── Remote Data ─────────────────────────────────────────────────────

  const [, createOrder] = useGroupOrderCreateOrderMutation();

  // ─── Derived ─────────────────────────────────────────────────────────

  const locationDetails = useGroupOrderLocationDetails({ deliveryAddressId, restaurantId });

  const orderChannel = locationDetails?.orderChannel;
  const locationIconName = orderChannel ? LOCATION_ICON_NAMES[orderChannel] : 'IconLocationPin';

  // ─── Form ────────────────────────────────────────────────────────────

  const { form, maxLengths, rules } = useGroupOrderSetupForm();
  const { control, formState } = form;
  const { errors, isValid, isSubmitting } = formState;

  // ─── Handlers ────────────────────────────────────────────────────────

  const closeModal = useCallback(() => {
    router.back();
  }, [router]);

  const navigateToLocationSearch = useCallback(() => {
    router.navigate('/group-ordering/create/search-location');
  }, [router]);

  const submitGroupOrder = useCallback(
    async (formData: GroupOrderSetupFormData) => {
      const location = locationDetails?.location;
      const restaurantId = Number(location?.restaurant?.id);

      if (!restaurantId) return;

      if (!Number.isFinite(restaurantId)) {
        Alert.alert('Unable to create group order');

        return;
      }

      const response = await createOrder({
        input: {
          restaurantId,
          deliveryOrderDetail: locationDetails?.deliveryOrderDetail,
          multipartyOrderDetails: { orderName: formData.orderName, hostName: formData.hostName },
        },
      });

      const result = response.data?.createOrder.__typename;

      if (response.error || !result) {
        alert('Unable to create group order');

        return;
      }

      alert(result);
      closeModal();
    },
    [createOrder, locationDetails, closeModal],
  );

  // ─────────────────────────────────────────────────────────────────────

  const isSubmitDisabled = !isValid || !locationDetails?.location;

  const containerStyles = [styles.container, match([undefined, styles.containerSM])];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <TransparentModal size="medium" fitHeight onClose={closeModal}>
      <ScrollView>
        <Container style={containerStyles}>
          {locationDetails?.isFetching ? (
            <View style={styles.loadingContainer}>
              <LoadingAnimation size={match(['large', 'small'])} />
            </View>
          ) : null}

          <View>
            <IconLink
              name="IconClose"
              width={40}
              height={40}
              palette="muted"
              onPress={closeModal}
              accessibilityLabel={formatMessage(messages.closeA11yLabel)}
              style={styles.closeButton}
            />

            <DisplayText sizeMatch={['32']}>
              <FormattedMessage {...messages.title} />
            </DisplayText>

            <BodyText sizeMatch={['16']} style={styles.subtitle}>
              <FormattedMessage {...messages.subtitle} />
            </BodyText>
          </View>

          <View style={styles.section}>
            <BodyText sizeMatch={['16']} bold>
              <FormattedMessage {...messages.details} />
            </BodyText>

            <View style={styles.fields}>
              <Form.TextField
                name="orderName"
                control={control}
                rules={rules.orderName}
                label={formatMessage(messages.orderNameLabel)}
                notice={errors.orderName?.message}
                maxLength={maxLengths.orderName}
                hideSubmitButton
                disabled={isSubmitting}
                autoCapitalize="words"
                clearButtonType="icon"
              />

              <Form.TextField
                name="hostName"
                control={control}
                rules={rules.name}
                placeholder={formatMessage(messages.hostNameLabel)}
                helperText={formatMessage(messages.hostNameHelperText)}
                notice={errors.hostName?.message}
                maxLength={maxLengths.name}
                hideSubmitButton
                disabled={isSubmitting}
                autoCapitalize="words"
                clearButtonType="icon"
              />
            </View>
          </View>

          <View style={styles.divider} />

          <View style={styles.section}>
            <BodyText sizeMatch={['16']} bold>
              <FormattedMessage
                {...messages.locationTitle}
                values={{ channel: locationDetails?.orderChannel }}
              />
            </BodyText>

            <TouchableOpacity
              ref={locationToggleRef}
              accessibilityRole="button"
              accessibilityLabel={formatMessage(messages.locationToggleA11yLabel)}
              disabled={isSubmitting}
              style={[styles.locationToggle, locationToggleWebStyles]}
              activeOpacity={0.75}
              onPress={navigateToLocationSearch}
            >
              <View
                style={[
                  styles.locationToggleBackground,
                  locationToggleBgContainerWebStyles,
                  isLocationRowHovered ? styles.locationToggleBgContainerHovered : undefined,
                ]}
              />

              <View style={styles.locationLabelContainer}>
                <Icon name={locationIconName} />

                <View style={styles.locationDetailsContainer}>
                  {locationDetails?.location ? (
                    <BodyText sizeMatch={['14']} bold>
                      {locationDetails?.location?.label}
                    </BodyText>
                  ) : (
                    <BodyText sizeMatch={['14']} bold>
                      <FormattedMessage {...messages.locationPlaceholder} />
                    </BodyText>
                  )}

                  {locationDetails?.location?.address ? (
                    <BodyText sizeMatch={['14']}>{locationDetails.location.address}</BodyText>
                  ) : null}

                  {locationDetails?.location?.secondaryAddress ? (
                    <BodyText sizeMatch={['14']}>
                      {locationDetails?.location.secondaryAddress}
                    </BodyText>
                  ) : null}
                </View>
              </View>

              <Icon name="IconCaretRight" />
            </TouchableOpacity>
          </View>

          <Button
            size="large-wide"
            disabled={isSubmitDisabled}
            isLoading={isSubmitting}
            hasDisabledStyle
            onPress={form.handleSubmit(submitGroupOrder)}
          >
            <FormattedMessage {...messages.submitCta} />
          </Button>
        </Container>
      </ScrollView>
    </TransparentModal>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  title: {
    defaultMessage: 'Group Ordering',
    description: 'Group Order Setup | Title',
  },
  subtitle: {
    defaultMessage: 'Confirm your group order details and send invites.',
    description: 'Group Order Setup | Subtitle',
  },
  closeA11yLabel: {
    defaultMessage: 'Close group ordering',
    description: 'Group Order Setup | Close button accessibility label',
  },
  details: {
    defaultMessage: 'Details',
    description: 'Group Order Setup | Details section title',
  },
  orderNameLabel: {
    defaultMessage: 'Group order name',
    description: 'Group Order Setup | Order name field label',
  },
  hostNameLabel: {
    defaultMessage: 'Your name',
    description: 'Group Order Setup | Host name field label',
  },
  hostNameHelperText: {
    defaultMessage: 'Your name, shown to your group',
    description: 'Group Order Setup | Host name helper text',
  },
  locationTitle: {
    defaultMessage:
      '{channel, select, delivery {Delivery location} outpost {Outpost location} pickup {Pickup location} other {Location}}',
    description: 'Group Order Setup | Location section title',
  },
  locationPlaceholder: {
    defaultMessage: 'Choose location',
    description: 'Group Order Setup | Location placeholder',
  },
  locationToggleA11yLabel: {
    defaultMessage: 'Choose location',
    description: 'Group Order Setup | Location row accessibility label',
  },
  submitCta: {
    defaultMessage: 'Send invites',
    description: 'Group Order Setup | Submit CTA',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const LOCATION_ICON_NAMES = {
  delivery: 'IconDelivery',
  outpost: 'IconPickupStrokeSm',
  pickup: 'IconPickupStrokeSm',
} as const satisfies Record<NonNullable<GroupOrderChannel>, IconName>;

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  scrollContainer: {
    minHeight: 300,
  },
  container: {
    flex: 1,
    paddingTop: theme.spacing['6'],
    paddingBottom: theme.spacing['8'],
    gap: theme.spacing['8'],
  },
  containerSM: {
    paddingHorizontal: theme.spacing['8'],
  },
  loadingContainer: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 1,
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
  closeButton: {
    alignSelf: 'flex-end',
    marginBottom: theme.spacing['4'],
  },
  titleContainer: {
    flex: 1,
    paddingTop: theme.spacing['8'],
    gap: theme.spacing['4'],
  },
  subtitle: {
    color: theme.colors.CHARCOAL,
  },
  section: {
    gap: theme.spacing['4'],
  },
  fields: {
    gap: theme.spacing['8'],
  },
  divider: {
    height: 10,
    marginHorizontal: -theme.spacing['8'],
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: theme.colors.GRAY,
  },
  locationToggle: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing['4'],
    borderRadius: theme.radius.medium,
    marginTop: theme.spacing['2'],
  },
  locationToggleBackground: {
    ...StyleSheet.absoluteFillObject,
    borderRadius: theme.radius.medium,
    margin: -theme.spacing['2'],
    padding: theme.spacing['2'],
  },
  locationToggleBgContainerHovered: {
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
  },
  locationLabelContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['4'],
  },
  locationDetailsContainer: {
    flex: 1,
  },
});

const locationToggleWebStyles = webOnlyStyles({
  outlineOffset: 10,
  outlineColor: theme.colors.GRAY,
});

const locationToggleBgContainerWebStyles = webOnlyStyles({
  transition: `background-color ${theme.transitions.base}ms ease-in-out`,
});

// ─── Types ───────────────────────────────────────────────────────────────────

type IconName = ComponentProps<typeof Icon>['name'];
